import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Info,
  QrCode,
  XCircle
} from 'lucide-react';
import { useToast } from '../../components/UI/Toast';
import { translateBackendError } from '../../utils/translateBackendError';

type AuthMethod = 'password' | 'sms';
// We assume people will click "Request PIN" to send SMS
const RECENT_PHONE_STORAGE_KEY = 'auth_recent_phones';
const MAX_RECENT_PHONES = 3;

type PendingConsentAction = 'sendCode' | 'submit' | null;
type FieldName = 'phone' | 'code' | 'password';
type FieldErrors = Partial<Record<FieldName, string>>;

interface AuthProps {
  isModal?: boolean;
  onClose?: () => void;
}

const Auth: React.FC<AuthProps> = ({ isModal = false, onClose }) => {
  const [authMethod, setAuthMethod] = useState<AuthMethod>('password');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [recentPhones, setRecentPhones] = useState<string[]>([]);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);
  const [phonePrefilledFromHistory, setPhonePrefilledFromHistory] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showAgreementConfirm, setShowAgreementConfirm] = useState(false);
  const [pendingConsentAction, setPendingConsentAction] = useState<PendingConsentAction>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [authError, setAuthError] = useState<string>('');
  const [countdown, setCountdown] = useState(0);
  
  const { showToast, ToastWrapper } = useToast();

  const navigate = useNavigate();
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const phoneFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    try {
      const savedPhones = JSON.parse(localStorage.getItem(RECENT_PHONE_STORAGE_KEY) || '[]');
      if (!Array.isArray(savedPhones) || savedPhones.length === 0) {
        return;
      }

      const nextPhones = savedPhones
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .slice(0, MAX_RECENT_PHONES);

      if (nextPhones.length === 0) {
        return;
      }

      setRecentPhones(nextPhones);
      setPhone((currentPhone) => currentPhone || nextPhones[0]);
      setPhonePrefilledFromHistory(true);
    } catch (error) {
      console.error('Failed to load recent phones:', error);
    }
  }, []);

  useEffect(() => {
    if (!showPhoneSuggestions) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (phoneFieldRef.current && !phoneFieldRef.current.contains(event.target as Node)) {
        setShowPhoneSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPhoneSuggestions]);

  const filteredRecentPhones = useMemo(() => {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      return recentPhones;
    }

    return recentPhones.filter((savedPhone) => savedPhone.includes(trimmedPhone));
  }, [phone, recentPhones]);

  const rememberPhone = (rawPhone: string) => {
    const trimmedPhone = rawPhone.trim();
    if (!/^(\+\d{1,3}[- ]?)?\d{10,14}$/.test(trimmedPhone)) {
      return;
    }

    setRecentPhones((previousPhones) => {
      const nextPhones = [trimmedPhone, ...previousPhones.filter((item) => item !== trimmedPhone)]
        .slice(0, MAX_RECENT_PHONES);
      localStorage.setItem(RECENT_PHONE_STORAGE_KEY, JSON.stringify(nextPhones));
      return nextPhones;
    });
  };

  const clearFieldError = (field: FieldName) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: undefined };
    });
  };

  const validatePhone = () => {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      setFieldErrors(prev => ({ ...prev, phone: '请输入手机号' }));
      return false;
    }

    if (!/^(\+\d{1,3}[- ]?)?\d{10,14}$/.test(trimmedPhone)) {
      setFieldErrors(prev => ({ ...prev, phone: '请输入有效的手机号' }));
      return false;
    }

    clearFieldError('phone');
    return true;
  };

  const validateDynamicField = () => {
    if (authMethod === 'sms') {
      const trimmedCode = code.trim();
      if (!trimmedCode) {
        setFieldErrors(prev => ({ ...prev, code: '请输入验证码' }));
        return false;
      }
      clearFieldError('code');
      return true;
    }

    if (!password.trim()) {
      setFieldErrors(prev => ({ ...prev, password: '请输入密码' }));
      return false;
    }

    clearFieldError('password');
    return true;
  };

  const maybeShowAgreementConfirm = (action: PendingConsentAction) => {
    if (agreedToTerms) return false;
    setPendingConsentAction(action);
    setShowAgreementConfirm(true);
    return true;
  };

  const sendCodeRequest = async () => {
    if (!validatePhone()) {
      return;
    }

    try {
      const response = await apiClient.post<any>('/api/v1/auth/sms/send', { phone: phone.trim() });
      if (response.success) {
        rememberPhone(phone.trim());
        showToast(`验证码已发送到 ${phone.trim()}`, 'success');
        setCountdown(60);
      } else {
        showToast(translateBackendError(response.data?.error || response.error, '验证码发送失败，请稍后重试'), 'error');
      }
    } catch (err: any) {
      showToast('验证码发送失败，请检查网络后重试', 'error');
    }
  };

  const handleSendCode = async () => {
    if (countdown > 0) return;
    if (maybeShowAgreementConfirm('sendCode')) {
      return;
    }

    await sendCodeRequest();
  };

  const handleWeChatLogin = async () => {
    // Mock WeChat Login
    try {
      const response = await apiClient.post<any>('/api/v1/auth/login/wechat', { code: 'mock-code' });
      if (response.success) {
        showToast('微信登录成功', 'success');
        navigate('/');
      } else {
        showToast(translateBackendError(response.error, '微信登录仍在开发中'), 'info');
      }
    } catch (err) {
      showToast('微信登录仍在开发中', 'info');
    }
  };

  const submitLoginRequest = async () => {
    setAuthError('');

    const isPhoneValid = validatePhone();
    const isDynamicFieldValid = validateDynamicField();
    if (!isPhoneValid || !isDynamicFieldValid) {
      return;
    }

    try {
      let endpoint = '';
      let payload = {};

      if (authMethod === 'password') {
        endpoint = '/api/v1/auth/login/password';
        payload = { phone: phone.trim(), password };
      } else {
        endpoint = '/api/v1/auth/login/sms';
        payload = { phone: phone.trim(), code: code.trim() };
      }

      const response = await apiClient.post<any>(endpoint, payload);

      if (response.success) {
        rememberPhone(phone.trim());
        localStorage.setItem('token', response.data.token);
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
        window.dispatchEvent(new Event('auth-change'));
        showToast('登录成功', 'success');
        if (isModal && onClose) {
          setTimeout(() => onClose(), 1000);
        } else {
          setTimeout(() => navigate('/'), 1000);
        }
      } else {
        setAuthError(translateBackendError(response.error, '登录失败，请检查输入'));
      }
    } catch (error: any) {
      setAuthError(translateBackendError(error?.message, '网络或服务器错误，请稍后重试'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (maybeShowAgreementConfirm('submit')) {
      return;
    }

    await submitLoginRequest();
  };

  const handleAgreementConfirm = async () => {
    const action = pendingConsentAction;
    setAgreedToTerms(true);
    setPendingConsentAction(null);
    setShowAgreementConfirm(false);

    if (action === 'sendCode') {
      await sendCodeRequest();
      return;
    }

    if (action === 'submit') {
      await submitLoginRequest();
    }
  };

  const inputClassName = (hasError?: string) =>
    `appearance-none relative block w-full px-4 py-4 border text-slate-900 rounded-2xl transition-all focus:outline-none focus:ring-2 sm:text-sm ${
      hasError
        ? 'border-red-300 bg-red-50/40 placeholder-red-300 focus:border-red-400 focus:ring-red-100'
        : 'border-slate-200 bg-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-100'
    }`;

  const renderFieldError = (message?: string) =>
    message ? (
      <div className="mt-2 inline-flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-600 shadow-sm">
        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>{message}</span>
      </div>
    ) : null;

  const authContent = (
    <div className="relative w-full max-w-[480px] overflow-hidden rounded-[30px] border border-slate-100 bg-white shadow-[0_32px_90px_rgba(15,23,42,0.18)]">
      <div className="flex min-h-[360px]">
        {/* WeChat scan-to-login panel hidden until the wechat flow is
            actually wired up. handleWeChatLogin still posts mock-code
            so showing the QR would mislead users. Re-enable when
            /api/v1/auth/login/wechat is implemented. */}

        <div className="flex w-full flex-col justify-center px-7 py-8 md:px-9">

          <div className="mb-6 flex justify-center gap-6 border-b border-slate-100 pb-3">
            <button
              type="button"
              className={`relative pb-2 text-base font-medium transition-colors ${authMethod === 'password' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
              onClick={() => {
                setAuthMethod('password');
                setAuthError('');
                setFieldErrors({});
              }}
            >
              密码登录
              {authMethod === 'password' && <span className="absolute bottom-[-13px] left-0 h-0.5 w-full rounded-full bg-blue-600"></span>}
            </button>
            <button
              type="button"
              className={`relative pb-2 text-base font-medium transition-colors ${authMethod === 'sms' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
              onClick={() => {
                setAuthMethod('sms');
                setAuthError('');
                setFieldErrors({});
              }}
            >
              验证码登录
              {authMethod === 'sms' && <span className="absolute bottom-[-13px] left-0 h-0.5 w-full rounded-full bg-blue-600"></span>}
            </button>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="relative" ref={phoneFieldRef}>
              <label className="sr-only">手机号</label>
              <div
                className={`relative flex overflow-hidden rounded-2xl border bg-white shadow-sm transition-all focus-within:ring-2 ${
                  fieldErrors.phone
                    ? 'border-red-300 bg-red-50/40 focus-within:border-red-400 focus-within:ring-red-100'
                    : 'border-slate-200 focus-within:border-blue-500 focus-within:ring-blue-100'
                }`}
              >
                <div className={`flex items-center gap-2 border-r px-4 text-sm text-slate-500 ${fieldErrors.phone ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50'}`}>
                  <span>+86</span>
                  <ChevronDown className="h-4 w-4" />
                </div>
                <input
                  ref={phoneInputRef}
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  className="block w-full flex-1 border-0 bg-transparent px-4 py-4 text-slate-900 outline-none placeholder-slate-400 focus:outline-none focus:ring-0 sm:text-sm"
                  placeholder="请输入手机号"
                  value={phone}
                  onFocus={() => {
                    if (filteredRecentPhones.length > 0) {
                      setShowPhoneSuggestions(true);
                    }

                    if (phonePrefilledFromHistory) {
                      requestAnimationFrame(() => phoneInputRef.current?.select());
                    }
                  }}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setPhonePrefilledFromHistory(false);
                    if (recentPhones.length > 0) {
                      setShowPhoneSuggestions(true);
                    }
                    clearFieldError('phone');
                    setAuthError('');
                  }}
                />
              </div>
              {showPhoneSuggestions && filteredRecentPhones.length > 0 && (
                <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                  {filteredRecentPhones.map((savedPhone) => (
                    <button
                      key={savedPhone}
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setPhone(savedPhone);
                        setPhonePrefilledFromHistory(false);
                        setShowPhoneSuggestions(false);
                        clearFieldError('phone');
                        setAuthError('');
                      }}
                      className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <span>{savedPhone}</span>
                      <span className="text-xs text-slate-400">最近使用</span>
                    </button>
                  ))}
                </div>
              )}
              {renderFieldError(fieldErrors.phone)}
            </div>

            {authMethod === 'sms' ? (
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    className={inputClassName(fieldErrors.code)}
                    placeholder="验证码"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      clearFieldError('code');
                      setAuthError('');
                    }}
                  />
                  {renderFieldError(fieldErrors.code)}
                </div>
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={countdown > 0}
                  className={`whitespace-nowrap rounded-2xl border px-5 py-4 text-sm font-semibold transition-colors ${
                    countdown > 0 
                      ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed' 
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {countdown > 0 ? `${countdown}s 后重试` : '获取验证码'}
                </button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`${inputClassName(fieldErrors.password)} pr-12`}
                    placeholder="请输入密码"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearFieldError('password');
                      setAuthError('');
                    }}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-4"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                    )}
                  </button>
                </div>
                {renderFieldError(fieldErrors.password)}
              </div>
            )}
            {authError && (
              <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            <button
              type="submit"
              className="mt-1 flex w-full justify-center rounded-2xl border border-transparent bg-black px-4 py-4 text-sm font-bold text-white shadow-md transition-all hover:bg-gray-800"
            >
              登录
            </button>

            <div className="flex items-start gap-3 pt-1">
              <button
                type="button"
                onClick={() => setAgreedToTerms(prev => !prev)}
                className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                  agreedToTerms
                    ? 'border-black bg-black text-white'
                    : 'border-slate-300 bg-white text-transparent hover:border-slate-400'
                }`}
                aria-pressed={agreedToTerms}
                aria-label="同意协议"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <p className="text-xs leading-6 text-slate-500">
                我已阅读并同意
                <span className="mx-1 font-medium text-slate-900">《用户协议》</span>
                和
                <span className="ml-1 font-medium text-slate-900">《隐私政策》</span>
              </p>
            </div>
          </form>
        </div>
      </div>

      {showAgreementConfirm && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 px-4 rounded-[30px] overflow-hidden">
          <div className="w-full max-w-md rounded-[28px] border border-slate-100 bg-white p-7 shadow-2xl animate-scale-in relative z-30">
            <div className="flex items-start gap-4">
              <div className="mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-[28px] font-semibold tracking-tight text-slate-900">服务协议及隐私保护</h3>
                <p className="mt-4 text-base leading-8 text-slate-700">
                  继续前请先确认你已阅读并同意
                  <span className="font-semibold text-slate-900">《用户协议》</span>
                  、
                  <span className="font-semibold text-slate-900">《隐私政策》</span>
                  。
                </p>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setPendingConsentAction(null);
                  setShowAgreementConfirm(false);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-8 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                不同意
              </button>
              <button
                type="button"
                onClick={handleAgreementConfirm}
                className="rounded-2xl bg-black px-8 py-3 text-base font-medium text-white transition-colors hover:bg-gray-800"
              >
                同意
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <ToastWrapper />
      
      {isModal ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="absolute inset-0" onClick={onClose}></div>
          <div className="relative z-10 flex w-full max-w-[480px] justify-center">
            {authContent}
          </div>
        </div>
      ) : (
        <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
          {authContent}
        </div>
      )}
    </>
  );
};

export default Auth;
